/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import type { ProcessGuide } from '@/types/process-guides.types';
import { GUIDE_CATEGORIES, PROCESS_GUIDES } from './process-guides-data';

interface GuideBrowserProps {
  onSelectGuide: (guide: ProcessGuide) => void;
  compact?: boolean;
}

const GuideBrowser: React.FC<GuideBrowserProps> = ({ onSelectGuide, compact = false }) => {
  const { t } = useTranslation('platform');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filteredGuides = useMemo(() => {
    let guides = PROCESS_GUIDES;

    if (selectedCategory) {
      guides = guides.filter((g) => g.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      guides = guides.filter((g) => {
        const title = t(`guides.${g.code}.title`, g.code).toLowerCase();
        return title.includes(q);
      });
    }

    return guides;
  }, [searchQuery, selectedCategory, t]);

  return (
    <div className={`guide-browser${compact ? ' guide-browser--compact' : ''}`}>
      {/* Search */}
      <div className="guide-browser-search">
        <Search size={16} className="guide-browser-search-icon" />
        <input
          type="text"
          className="guide-browser-search-input"
          placeholder={t('guides.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Category filter pills */}
      <div className="guide-browser-categories">
        <button
          className={`guide-browser-pill${selectedCategory === null ? ' guide-browser-pill--active' : ''}`}
          onClick={() => setSelectedCategory(null)}
          type="button"
        >
          {t('guides.categories.all', 'All')}
        </button>
        {GUIDE_CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`guide-browser-pill${selectedCategory === cat ? ' guide-browser-pill--active' : ''}`}
            onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
            type="button"
          >
            {t(`guides.categories.${cat}`, cat)}
          </button>
        ))}
      </div>

      {/* Guide cards */}
      <div className="guide-browser-list">
        {filteredGuides.map((guide) => {
          const title = t(`guides.${guide.code}.title`, guide.code);
          const description = t(`guides.${guide.code}.description`, '');
          const categoryLabel = t(`guides.categories.${guide.category}`, guide.category);

          return (
            <button
              key={guide.code}
              className="guide-browser-card"
              onClick={() => onSelectGuide(guide)}
              type="button"
            >
              <div className="guide-browser-card-header">
                <span className="guide-browser-card-title">{title}</span>
                <span className="guide-browser-card-badge">{categoryLabel}</span>
              </div>
              {description && (
                <p className="guide-browser-card-desc">{description}</p>
              )}
              <span className="guide-browser-card-steps">
                {t('guides.stepsCount', { count: guide.steps.length })}
              </span>
            </button>
          );
        })}

        {filteredGuides.length === 0 && (
          <div className="guide-browser-empty">
            {t('guides.noResults', 'No guides found.')}
          </div>
        )}
      </div>

      <style>{`
        .guide-browser {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        /* Search */
        .guide-browser-search {
          position: relative;
        }
        .guide-browser-search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--color-text-muted);
          pointer-events: none;
        }
        .guide-browser-search-input {
          width: 100%;
          padding: 0.5rem 0.75rem 0.5rem 2.25rem;
          background: var(--input-bg);
          border: 1px solid var(--input-border);
          border-radius: var(--radius-md);
          color: var(--color-text-primary);
          font-size: 0.875rem;
          outline: none;
          box-sizing: border-box;
        }
        .guide-browser-search-input:focus {
          border-color: var(--color-brand-primary);
          box-shadow: 0 0 0 2px var(--input-focus-ring);
        }
        .guide-browser-search-input::placeholder {
          color: var(--color-text-muted);
        }

        /* Category pills */
        .guide-browser-categories {
          display: flex;
          gap: 0.375rem;
          overflow-x: auto;
          padding-bottom: 0.25rem;
          scrollbar-width: thin;
        }
        .guide-browser-pill {
          flex-shrink: 0;
          padding: 0.25rem 0.625rem;
          border-radius: var(--radius-full);
          border: 1px solid var(--color-border-default);
          background: var(--color-bg-secondary);
          color: var(--color-text-secondary);
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.15s ease;
        }
        .guide-browser-pill:hover {
          border-color: var(--color-brand-primary);
          color: var(--color-brand-primary);
        }
        .guide-browser-pill--active {
          background: var(--color-brand-primary);
          border-color: var(--color-brand-primary);
          color: #fff;
        }
        .guide-browser-pill--active:hover {
          color: #fff;
        }

        /* Guide cards */
        .guide-browser-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          overflow-y: auto;
          max-height: 480px;
        }
        .guide-browser-card {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
          padding: 0.75rem;
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border-subtle);
          border-radius: var(--radius-md);
          cursor: pointer;
          text-align: left;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .guide-browser-card:hover {
          border-color: var(--color-brand-primary);
          box-shadow: var(--shadow-sm);
        }
        .guide-browser-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
        }
        .guide-browser-card-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }
        .guide-browser-card-badge {
          flex-shrink: 0;
          padding: 0.125rem 0.5rem;
          border-radius: var(--radius-full);
          background: var(--color-bg-tertiary);
          color: var(--color-text-muted);
          font-size: 0.6875rem;
          font-weight: 500;
        }
        .guide-browser-card-desc {
          margin: 0;
          color: var(--color-text-secondary);
          font-size: 0.8125rem;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .guide-browser-card-steps {
          color: var(--color-text-muted);
          font-size: 0.75rem;
        }
        .guide-browser-empty {
          text-align: center;
          padding: 2rem 1rem;
          color: var(--color-text-muted);
          font-size: 0.875rem;
        }

        /* Compact mode */
        .guide-browser--compact .guide-browser-search-input {
          font-size: 0.8125rem;
          padding: 0.375rem 0.625rem 0.375rem 2rem;
        }
        .guide-browser--compact .guide-browser-pill {
          font-size: 0.6875rem;
          padding: 0.1875rem 0.5rem;
        }
        .guide-browser--compact .guide-browser-card {
          padding: 0.5rem 0.625rem;
          gap: 0.25rem;
        }
        .guide-browser--compact .guide-browser-card-title {
          font-size: 0.8125rem;
        }
        .guide-browser--compact .guide-browser-card-desc {
          font-size: 0.75rem;
        }
        .guide-browser--compact .guide-browser-list {
          max-height: 360px;
        }
      `}</style>
    </div>
  );
};

export default GuideBrowser;
